import {
  reatomAsync,
  withStatusesAtom,
  withDataAtom,
  atom,
  action,
  onDisconnect,
  withReset,
  onUpdate,
  withAbort,
} from '@reatom/framework';
import { formatServerMoves, getGameByIdRequest, Storage } from '@whitesharx/starfall-frontend-core';
import type { Api, Key } from '@whitesharx/starfall-frontend-chessground';
import { analyzeRequest } from './requests/analyze';
import { AnalysisGame, ProcessedAnalyzedMove, ContextfullAnalyzedMove } from './types';
import { buildAnalyzedMovesKey, group, processContextfullAnalyzedMove } from './utils';
import { getAnalysisLimitRequest } from './requests/getLimits';
import { startAnalysisRequest, AnalyzLimitRequestOptions } from './requests/startAnalysis';
import { RewindType } from './components/Rewind/Rewind';

const PARALLEL_REQUESTS_COUNT = 6;

let rewindPlayingInterval: NodeJS.Timeout;

export const cgApiAtom = atom<Api | null>(null, 'cgApiAtom').pipe(withReset());
onDisconnect(cgApiAtom, cgApiAtom.reset);

export const analysisDisabledAtom = atom(false).pipe(withReset());
onDisconnect(analysisDisabledAtom, analysisDisabledAtom.reset);

export const gameAnalyzedAtom = atom(false).pipe(withReset());

export const selectedMoveAtom = atom<ProcessedAnalyzedMove | null>(null, 'selectedMoveAtom').pipe(withReset());
onDisconnect(selectedMoveAtom, selectedMoveAtom.reset);

export const getAnalysisGameAction = reatomAsync(
  (ctx, id: string) =>
    getGameByIdRequest(id).then(
      (data) =>
        ({
          ...data.play,
          rawMoves: data.play.moves,
          moves: formatServerMoves(data.play.moves, data.play.fen),
          startFen: data.play.moves[0]?.fen || data.play.fen,
        } as AnalysisGame)
    ),
  'getAnalysisGameAction'
).pipe(withStatusesAtom(), withDataAtom());

onDisconnect(getAnalysisGameAction.dataAtom, (ctx) => {
  getAnalysisGameAction.dataAtom(ctx, undefined);
  analyzeGameAction.abort(ctx);
  gameAnalyzedAtom.reset(ctx);
  clearInterval(rewindPlayingInterval);
});

onUpdate(getAnalysisGameAction.onFulfill, (ctx, analysisGame) => {
  const savedAnalyzedMoves = Storage.getItem<ContextfullAnalyzedMove[]>(buildAnalyzedMovesKey(analysisGame.id));
  const cgApi = ctx.get(cgApiAtom);

  if (savedAnalyzedMoves && cgApi) {
    savedAnalyzedMoves.forEach((savedMove) => {
      const updatedAnalyzedMove = processContextfullAnalyzedMove({ move: savedMove, game: analysisGame, cgApi });
      analysisGame.moves[savedMove.index] = {
        ...analysisGame.moves[savedMove.index],
        ...updatedAnalyzedMove,
      };
    });

    getAnalysisGameAction.dataAtom(ctx, analysisGame);
    gameAnalyzedAtom(ctx, true);
  }
});

export const analysisProgressAtom = atom(0, 'analysisProgressAtom').pipe(withReset());
onDisconnect(analysisProgressAtom, analysisProgressAtom.reset);

export const analyzeGameAction = reatomAsync(async (ctx) => {
  let started = Date.now();
  const movesToPersist: Array<ContextfullAnalyzedMove> = [];

  const cgApi = ctx.get(cgApiAtom);
  const game = ctx.get(getAnalysisGameAction.dataAtom);

  if (!cgApi || !game) {
    return;
  }

  const moves = game.rawMoves.map((move) => cgApi.convertToFullMove(move));
  const grouped = group(moves, PARALLEL_REQUESTS_COUNT);

  for await (const batch of grouped) {
    if (ctx.controller.signal.aborted) {
      break;
    }

    const batchIndex = grouped.indexOf(batch);

    const requests = batch.map((move, moveIndex) =>
      analyzeRequest(move).then((data) => {
        if (ctx.controller.signal.aborted) {
          return;
        }

        analysisProgressAtom(ctx, (state) => state + 100 / moves.length);

        return { ...data, index: moveIndex + batchIndex * PARALLEL_REQUESTS_COUNT, fen: move.fen };
      })
    );

    await Promise.allSettled(requests).then((result) =>
      result.forEach((response) => {
        if (response.status === 'rejected') {
          window.Sentry?.captureException(new Error(`Got rejected promise during analysing ${response}`));
          return;
        }

        if (!response.value || ctx.controller.signal.aborted) {
          return;
        }

        const game = ctx.get(getAnalysisGameAction.dataAtom)!;

        const contextfullMove = response.value;
        movesToPersist.push(contextfullMove);

        const updatedAnalyzedMove = processContextfullAnalyzedMove({ move: contextfullMove, game, cgApi });
        const copiedMoves = [...game!.moves];

        copiedMoves[contextfullMove.index] = {
          ...copiedMoves[contextfullMove.index],
          ...updatedAnalyzedMove,
        };

        getAnalysisGameAction.dataAtom(ctx, {
          ...game!,
          moves: copiedMoves,
        });
      })
    );
  }

  if (!ctx.controller.signal.aborted) {
    gameAnalyzedAtom(ctx, true);
    Storage.setItem(buildAnalyzedMovesKey(game.id), movesToPersist);
  }

  console.log('Analysis completed in: ', (Date.now() - started) / 1000 + ' s');
  return true;
}, 'analyzeGameAction').pipe(withStatusesAtom(), withAbort());

export const getAnalysisLimitAction = reatomAsync(getAnalysisLimitRequest, 'getAnalysisLimitAction').pipe(
  withStatusesAtom(),
  withDataAtom()
);

export const analysisStartDateAction = action(async (ctx, options: AnalyzLimitRequestOptions) => {
  const date = await startAnalysisRequest(options);
  getAnalysisLimitAction.dataAtom(ctx, date);

  return date;
}, 'analysisStartDateAction');

export const selectMoveAction = action((ctx, move: ProcessedAnalyzedMove | null) => {
  const cgApi = ctx.get(cgApiAtom);
  const game = ctx.get(getAnalysisGameAction.dataAtom);

  if (!move) {
    selectedMoveAtom(ctx, null);
    cgApi?.set({ fen: game!.startFen, lastMove: undefined });
    cgApi?.setAutoShapes([]);
  } else {
    selectedMoveAtom(ctx, move);

    cgApi?.set({
      fen: move.fen,
      lastMove: { san: move.san, fen: move.beforeMoveFen },
      highlight: { moveGrade: move.grade },
    });

    if (move.best) {
      const orig = (move.best.fullMove[0] + move.best.fullMove[1]) as Key;
      const dest = (move.best.fullMove[2] + move.best.fullMove[3]) as Key;

      cgApi?.setAutoShapes([{ orig, dest, brush: 'green' }]);
    } else {
      cgApi?.setAutoShapes([]);
    }
  }
}, 'selectMoveAction');

export const rewindAction = action((ctx, type: RewindType) => {
  const cgApi = ctx.get(cgApiAtom);
  const game = ctx.get(getAnalysisGameAction.dataAtom);
  const selectedMove = ctx.get(selectedMoveAtom);

  if (!cgApi || !game) {
    return;
  }

  if (type === 'start') {
    if (!selectedMove) {
      return;
    }

    selectMoveAction(ctx, null);
  }

  if (type === 'previous') {
    if (!selectedMove) {
      return;
    }

    const selectedIndex = game.moves.findIndex((move) => move.fen === selectedMove?.fen);

    selectMoveAction(ctx, game.moves[selectedIndex - 1] || null);
  }

  if (type === 'next') {
    const selectedIndex = game.moves.findIndex((move) => move.fen === selectedMove?.fen);
    const next = game.moves[selectedIndex + 1];

    if (!next) {
      return;
    }

    selectMoveAction(ctx, next);
  }

  if (type === 'end') {
    selectMoveAction(ctx, game.moves[game.moves.length - 1]);
  }

  if (type === 'play') {
    rewindPlayingInterval = setInterval(() => {
      const selected = ctx.get(selectedMoveAtom);
      const selectedIndex = game!.moves.findIndex((move) => move.fen === selected?.fen);
      const next = game!.moves[selectedIndex + 1];

      if (next) {
        selectMoveAction(ctx, next);
      }
    }, 1000);
  }

  if (type === 'pause') {
    clearInterval(rewindPlayingInterval);
  }
}, 'rewindAction');
